import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, MapPin, Clock, Building2, ChevronRight, ArrowLeft, Send, Loader2, User, Phone, Mail, FileText, Upload, X, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const typeColors = { 'Full-time': 'bg-[#5C7A3A]/10 text-[#5C7A3A]', 'Part-time': 'bg-[#C4844A]/10 text-[#C4844A]', 'Contract': 'bg-blue-50 text-blue-700', 'Internship': 'bg-purple-50 text-purple-700' };

const Careers = () => {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApply, setShowApply] = useState(null);
  const [form, setForm] = useState({ name: '', mobile: '', email: '', location: '', experience: '', message: '' });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API}/api/careers/jobs`).then(r => r.json()).then(setJobs).catch(() => {});
  }, []);

  const handleApply = async (e) => {
    e.preventDefault();
    if (!resume) { setError('Please upload your resume'); return; }
    const ext = resume.name.split('.').pop().toLowerCase();
    if (!['pdf', 'doc', 'docx'].includes(ext)) { setError('Resume must be PDF, DOC, or DOCX'); return; }
    if (resume.size > 5 * 1024 * 1024) { setError('Resume must be under 5MB'); return; }

    setSubmitting(true); setError('');
    const fd = new FormData();
    fd.append('job_id', showApply.id);
    fd.append('name', form.name);
    fd.append('mobile', form.mobile);
    fd.append('email', form.email);
    fd.append('location', form.location);
    fd.append('experience', form.experience);
    fd.append('message', form.message);
    fd.append('resume', resume);

    try {
      const res = await fetch(`${API}/api/careers/apply`, { method: 'POST', body: fd });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Submission failed'); }
      setSubmitted(true);
    } catch (err) { setError(err.message); }
    setSubmitting(false);
  };

  // Success State
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center px-4 pt-24 pb-12" data-testid="careers-success">
        <SEO title="Application Submitted" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full bg-white rounded-2xl border border-[#E8DFD3] shadow-lg p-8 text-center">
          <Check size={56} className="mx-auto text-[#5C7A3A] mb-4" />
          <h2 className="text-2xl font-bold text-[#2C1810] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Application Submitted!</h2>
          <p className="text-sm text-[#7a6555] mb-6">Thank you for your interest. Our HR team will review your application and get back to you.</p>
          <Button onClick={() => { setSubmitted(false); setShowApply(null); setSelectedJob(null); setForm({ name: '', mobile: '', email: '', location: '', experience: '', message: '' }); setResume(null); }} className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer" data-testid="back-to-careers-btn">View More Openings</Button>
        </motion.div>
      </div>
    );
  }

  // Job Detail View
  if (selectedJob) {
    return (
      <div className="min-h-screen bg-[#FDF8F0] pt-28 pb-12" data-testid="job-detail-page">
        <SEO title={selectedJob.title} />
        <div className="max-w-3xl mx-auto px-4">
          <button onClick={() => setSelectedJob(null)} className="inline-flex items-center gap-1.5 text-sm text-[#5C7A3A] font-medium hover:underline mb-6 cursor-pointer" data-testid="back-to-jobs"><ArrowLeft size={14} /> Back to Careers</button>
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
            <div className="bg-white rounded-2xl border border-[#E8DFD3] shadow-lg overflow-hidden">
              <div className="bg-[#2C1810] px-6 py-6">
                <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{selectedJob.title}</h1>
                <div className="flex flex-wrap gap-3 text-sm text-white/70">
                  <span className="flex items-center gap-1"><Building2 size={13} /> {selectedJob.department}</span>
                  <span className="flex items-center gap-1"><MapPin size={13} /> {selectedJob.location}</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${typeColors[selectedJob.employment_type] || 'bg-gray-100 text-gray-700'}`}>{selectedJob.employment_type}</span>
                </div>
              </div>
              <div className="p-6 space-y-5">
                {selectedJob.description && <div><h3 className="text-sm font-bold text-[#2C1810] mb-2">About the Role</h3><p className="text-sm text-[#4a3828] leading-relaxed whitespace-pre-line">{selectedJob.description}</p></div>}
                {selectedJob.responsibilities && <div><h3 className="text-sm font-bold text-[#2C1810] mb-2">Responsibilities</h3><p className="text-sm text-[#4a3828] leading-relaxed whitespace-pre-line">{selectedJob.responsibilities}</p></div>}
                {selectedJob.qualifications && <div><h3 className="text-sm font-bold text-[#2C1810] mb-2">Qualifications & Skills</h3><p className="text-sm text-[#4a3828] leading-relaxed whitespace-pre-line">{selectedJob.qualifications}</p></div>}
                {selectedJob.experience && <div><h3 className="text-sm font-bold text-[#2C1810] mb-2">Experience Required</h3><p className="text-sm text-[#4a3828]">{selectedJob.experience}</p></div>}
                {selectedJob.deadline && <div><h3 className="text-sm font-bold text-[#2C1810] mb-2">Application Deadline</h3><p className="text-sm text-[#C4844A] font-medium">{selectedJob.deadline}</p></div>}
                <Button onClick={() => setShowApply(selectedJob)} className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white font-semibold px-8 py-4 cursor-pointer" data-testid="apply-from-detail">Apply Now <Send size={14} className="ml-2" /></Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Main Careers Listing
  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="careers-page">
      <SEO title="Careers" description="Join Nistala's Group — explore career opportunities in food tech, agriculture, operations, and more." />

      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#2C1810]" />
        <div className="relative max-w-5xl mx-auto px-4 text-center pt-14 pb-4">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs tracking-[0.25em] uppercase text-[#D4A574] mb-3">Join Our Team</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-4xl sm:text-5xl font-bold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            <span className="text-[#D4A574]">Careers</span> at Nistala's
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-base text-white/60 max-w-xl mx-auto">
            Be part of a mission-driven team transforming India's food ecosystem. We're looking for passionate people who believe in sustainable, healthy food for all.
          </motion.p>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-10">
        <div className="max-w-4xl mx-auto px-4">
          {jobs.length === 0 ? (
            <Card className="border border-[#E8DFD3] bg-white shadow-sm" data-testid="no-jobs">
              <CardContent className="p-12 text-center">
                <Briefcase size={48} className="mx-auto text-[#D4A574] mb-4" />
                <h2 className="text-xl font-bold text-[#2C1810] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>No Openings Right Now</h2>
                <p className="text-sm text-[#7a6555] max-w-md mx-auto">We don't have any open positions at the moment, but we're always growing. Check back soon or send your resume to <a href="mailto:info@nistalasoh.com" className="text-[#5C7A3A] font-semibold hover:underline">info@nistalasoh.com</a></p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-[#7a6555] mb-2">{jobs.length} open position{jobs.length !== 1 ? 's' : ''}</p>
              {jobs.map((job, idx) => (
                <motion.div key={job.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.08 }}>
                  <Card className="border border-[#E8DFD3] bg-white shadow-sm hover:shadow-lg hover:border-[#D4A574]/30 transition-all duration-300" data-testid={`job-card-${job.id}`}>
                    <CardContent className="p-5">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-[#2C1810] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>{job.title}</h3>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-[#7a6555]">
                            <span className="flex items-center gap-1"><Building2 size={12} /> {job.department}</span>
                            <span className="flex items-center gap-1"><MapPin size={12} /> {job.location}</span>
                            <span className={`px-2 py-0.5 rounded-full font-semibold ${typeColors[job.employment_type] || 'bg-gray-100 text-gray-700'}`}>{job.employment_type}</span>
                          </div>
                          {job.description && <p className="text-sm text-[#7a6555] mt-2 line-clamp-2">{job.description}</p>}
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button onClick={() => setSelectedJob(job)} variant="outline" className="border-[#E8DFD3] text-[#2C1810] hover:bg-[#FDF8F0] cursor-pointer text-xs" data-testid={`view-details-${job.id}`}>View Details</Button>
                          <Button onClick={() => setShowApply(job)} className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer text-xs" data-testid={`apply-btn-${job.id}`}>Apply Now</Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Apply Modal */}
      <AnimatePresence>
        {showApply && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowApply(null)} />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="relative w-full max-w-lg z-10 max-h-[90vh] overflow-y-auto">
              <Card className="border border-[#E8DFD3] shadow-2xl bg-white overflow-hidden">
                <div className="bg-[#2C1810] px-6 py-5 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Apply for Position</h3>
                    <p className="text-xs text-[#D4A574] mt-0.5">{showApply.title} — {showApply.department}</p>
                  </div>
                  <button onClick={() => setShowApply(null)} className="p-1.5 hover:bg-white/15 rounded-lg cursor-pointer" data-testid="close-apply-modal"><X size={18} className="text-white" /></button>
                </div>
                <CardContent className="p-6">
                  {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5 rounded-xl mb-4 flex items-center gap-2" data-testid="apply-error"><AlertCircle size={14} /> {error}</div>}
                  <form onSubmit={handleApply} className="space-y-3">
                    <div>
                      <label className="text-xs font-medium text-[#2C1810] mb-1 flex items-center gap-1"><User size={12} className="text-[#7a6555]" /> Full Name *</label>
                      <Input data-testid="apply-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your full name" required />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-[#2C1810] mb-1 flex items-center gap-1"><Phone size={12} className="text-[#7a6555]" /> Mobile *</label>
                        <Input data-testid="apply-mobile" type="tel" value={form.mobile} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 10); setForm(f => ({ ...f, mobile: v })); }} placeholder="10-digit number" maxLength={10} required />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-[#2C1810] mb-1 flex items-center gap-1"><Mail size={12} className="text-[#7a6555]" /> Email *</label>
                        <Input data-testid="apply-email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="your@email.com" required />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-[#2C1810] mb-1">Current Location</label>
                        <Input data-testid="apply-location" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="City" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-[#2C1810] mb-1">Experience</label>
                        <Input data-testid="apply-experience" value={form.experience} onChange={e => setForm(f => ({ ...f, experience: e.target.value }))} placeholder="e.g., 3 years" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#2C1810] mb-1 flex items-center gap-1"><FileText size={12} className="text-[#7a6555]" /> Resume * <span className="text-[#7a6555] font-normal">(PDF, DOC, DOCX — max 5MB)</span></label>
                      <div className="relative">
                        <input type="file" accept=".pdf,.doc,.docx" onChange={e => { setResume(e.target.files[0] || null); setError(''); }} className="hidden" id="resume-input" data-testid="resume-input" />
                        <label htmlFor="resume-input" className={`flex items-center gap-3 p-3 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${resume ? 'border-[#5C7A3A] bg-[#5C7A3A]/5' : 'border-[#E8DFD3] hover:border-[#D4A574]'}`}>
                          <Upload size={18} className={resume ? 'text-[#5C7A3A]' : 'text-[#7a6555]'} />
                          <span className="text-sm text-[#2C1810] truncate">{resume ? resume.name : 'Click to upload resume'}</span>
                          {resume && <span className="text-xs text-[#7a6555] ml-auto">{(resume.size / 1024).toFixed(0)} KB</span>}
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#2C1810] mb-1">Additional Message</label>
                      <Textarea data-testid="apply-message" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Tell us why you're interested..." rows={2} />
                    </div>
                    <Button type="submit" disabled={submitting} className="w-full bg-[#5C7A3A] hover:bg-[#4a6530] text-white font-semibold py-4 cursor-pointer" data-testid="submit-application-btn">
                      {submitting ? <><Loader2 size={14} className="animate-spin mr-2" /> Submitting...</> : <>Submit Application <Send size={14} className="ml-2" /></>}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Careers;
